import { NUREAD_GW_URL, getGatewayToken } from '$lib/auth.svelte'
import axios from 'axios'

const client = new axios.Axios({
  baseURL: NUREAD_GW_URL,
})

export type UserSettings = {
  hideFromDirectory: boolean
  blockInvitations: boolean
}

async function gatewayAuthHeaders() {
  const gatewayToken = await getGatewayToken()
  if (!gatewayToken) throw new Error('unauthenticated')
  return {
    Authorization: `Bearer ${gatewayToken}`,
    'Content-Type': 'application/json',
  }
}

function parseBody<T>(data: string | T): T {
  return typeof data === 'string' ? JSON.parse(data) : data
}

export async function getUserSettings(): Promise<UserSettings> {
  const headers = await gatewayAuthHeaders()
  const res = await client.get('/users/me/settings', { headers })
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) throw new Error('Failed to load settings')
  return parseBody<UserSettings>(res.data)
}

export async function updateUserSettings(
  settings: UserSettings,
): Promise<UserSettings> {
  const headers = await gatewayAuthHeaders()
  const res = await client.patch(
    '/users/me/settings',
    JSON.stringify(settings),
    { headers },
  )
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) throw new Error('Failed to update settings')
  return parseBody<UserSettings>(res.data)
}

export type DirectoryUser = {
  name: string
  hash: string
}

function errorDetail(data: unknown, fallback: string): string {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)?.detail || fallback
    } catch {
      return fallback
    }
  }
  return (data as { detail?: string })?.detail || fallback
}

/** Search the gateway user directory (GET /api/searchuser?q=…). */
export async function searchUsers(query: string): Promise<DirectoryUser[]> {
  const headers = await gatewayAuthHeaders()
  const q = encodeURIComponent(query.trim())
  const res = await client.get(`/searchuser?q=${q}`, { headers })
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status >= 400) {
    throw new Error(errorDetail(res.data, 'Failed to search users'))
  }
  const body = parseBody<{ users?: DirectoryUser[] }>(res.data)
  return Array.isArray(body?.users) ? body.users : []
}

/** Invite a directory user to a server (POST /api/invite). Owner-only. */
export async function inviteUser(
  name: string,
  hash: string,
  serverId: string,
): Promise<void> {
  const headers = await gatewayAuthHeaders()
  const res = await client.post(
    '/invite',
    JSON.stringify({ name, hash, serverId }),
    { headers },
  )
  if (res.status === 401) throw new Error('unauthenticated')
  if (res.status === 403) {
    throw new Error(errorDetail(res.data, 'User has blocked invitations'))
  }
  if (res.status >= 400) {
    throw new Error(errorDetail(res.data, 'Failed to send invite'))
  }
}
